// Szolgáltatás-hozzárendelés sor-akciók (jog- + állapot-vezérelt), FIX sorrendben:
// szerkesztés (ceruza) → másolás (két lap) → lezárás/feloldás (lakat) → törlés (kuka).
//
// A sorrend user-döntés: a leggyakoribb művelet áll elöl, a destruktív a sor végén. (Korábban a másolás
// állt a szerkesztés ELŐTT — az a „két azonos kiindulású akció egymás mellett" elvet követte, de a
// mindennapi használatban a szerkesztés az első, amit a kéz keres.)
//
// JOGOK: szerkesztés/másolás/lezárás `service_assignments.manage` (a másolásé a LÉTREHOZÁSÉ, mert ÚJ sort
// hoz létre), törlés `service_assignments.delete`. A törlést számlázott soron a BFF 409-cel tiltja → a
// lista toast + rollback jelzi.
//
// LEZÁRÁS vs. FELOLDÁS — a kettő ASZIMMETRIKUS, és ez szándékos:
//   • LEZÁRÁS csak `billing` + ismétlődő soron (a recurring proxyja a `billingFrequency` megléte; a BFF a
//     többit 422-zi) — a lezárás azt mondja ki, hogy a SZÁMLÁZÁS véget ér, ennek máshol nincs értelme;
//   • FELOLDÁS MINDEN lezárt soron, a nyilvántartás-típuson is. Élesben **385 lezárt nyilvántartás-sor**
//     van (a legacy-ből örökölt záró dátummal) — ha a feloldás ugyanazt a szűkítést kapná, lakat állna
//     rajtuk, amit senki nem tud kinyitni.
import { DocumentDuplicateIcon, LockClosedIcon, LockOpenIcon } from '@heroicons/react/24/outline';
import { type TFunction } from 'i18next';

import {
  DeleteRowAction,
  EditRowAction,
  RowActionButton,
  RowActions,
} from '@/components/shared/list-row-actions';

export interface ServiceAssignmentRowActionsRow {
  id: string;
  type: string;
  billingFrequency: string | null;
  billingEndDate: string | null;
  /** A díjtípus — az ESETI díj (`one_time`) sosem „lezárt" (l. `isAssignmentClosed`). */
  billingType?: string | null;
}

/**
 * LE VAN-E ZÁRVA a sor. A jelölő a `billingEndDate` — élesben EZ hordozza a lezárást mindkét típuson
 * (mérve: 591 lezárt sorból 587-nél csak ez van kitöltve, a `contractEndDate` 4 soron áll mellette).
 * Egy helyen él, mert a lista BADGE-e és a sor-AKCIÓ ugyanazt a kérdést teszi fel.
 *
 * ⚠️ ESETI DÍJ (`one_time`) SOSEM „lezárt" (user-döntés): az egyetlen dátuma — a „Számlázás dátuma" — a
 * számlázási kezdet ÉS vég is, tehát a záró dátum itt a számlázás napja, nem lezárás. Se jelvény, se lezárás /
 * feloldás (a szerver a feloldást 422-zi).
 */
export function isAssignmentClosed(row: {
  billingEndDate: string | null;
  billingType?: string | null;
}): boolean {
  if (row.billingType === 'one_time') return false;
  return row.billingEndDate !== null && row.billingEndDate !== '';
}

interface ServiceAssignmentRowActionsProps<T extends ServiceAssignmentRowActionsRow> {
  row: T;
  canManage: boolean;
  canDelete: boolean;
  onClone: (row: T) => void;
  onEdit: (row: T) => void;
  onClose: (row: T) => void;
  onReopen: (row: T) => void;
  onDelete: (row: T) => void;
  t: TFunction;
}

export function ServiceAssignmentRowActions<T extends ServiceAssignmentRowActionsRow>({
  row,
  canManage,
  canDelete,
  onClone,
  onEdit,
  onClose,
  onReopen,
  onDelete,
  t,
}: ServiceAssignmentRowActionsProps<T>): JSX.Element {
  const closed = isAssignmentClosed(row);
  // Lezárás CSAK billing + recurring (a gyakoriság megléte a recurring proxyja) ÉS csak nyitott soron.
  const showClose = canManage && !closed && row.type === 'billing' && row.billingFrequency !== null;
  const showReopen = canManage && closed;

  if (!canManage && !canDelete) return <span className="sr-only">—</span>;

  return (
    <RowActions>
      {canManage && (
        <EditRowAction label={t('serviceAssignments.edit')} onClick={() => onEdit(row)} />
      )}
      {canManage && (
        <RowActionButton
          label={t('serviceAssignments.clone.action')}
          icon={DocumentDuplicateIcon}
          onClick={() => onClone(row)}
        />
      )}
      {showClose && (
        <RowActionButton
          label={t('serviceAssignments.close')}
          icon={LockClosedIcon}
          onClick={() => onClose(row)}
        />
      )}
      {showReopen && (
        <RowActionButton
          label={t('serviceAssignments.reopen.action')}
          icon={LockOpenIcon}
          onClick={() => onReopen(row)}
        />
      )}
      {canDelete && (
        <DeleteRowAction label={t('serviceAssignments.delete')} onClick={() => onDelete(row)} />
      )}
    </RowActions>
  );
}
